/* eslint-disable max-len */
/*
Centralised error handling layer. This module contains hard-coded recognised
errors. These can be input into toast notifications or MUI alerts. All
errors passed to these components should be listed here to ensure a
consistent error-UX across the application
*/

import {
    GENERIC_DISPLAY_DETAIL,
    GENERIC_UNRECOGNISED_TECHNICAL_DETAIL,
    NO_PERMISSION_DISPLAY_DETAIL,
    NO_PERMISSION_TECHNICAL_DETAIL,
    PYDANTIC_ERROR_TECHNICAL_DETAIL,
    SUPPORT_REFER_ENTITY,
} from "./errorMessages"
import { AppError } from "./errorProcessors"
import { IAppError } from "./errorTypes"

/**
 * Fall back error for unrecognised errors. This should be used as a catch all.
 * @returns {AppError} - the generic error parse into an AppError
 */
export const badRequestError = (displayDetail?: string): AppError => {
    const displayDetailChecked = displayDetail || GENERIC_DISPLAY_DETAIL
    const error: IAppError = {
        message: "Ah! We are unable to perform this action for you",
        code: 400,
        displayDetail: displayDetailChecked,
        technicalDetail: displayDetailChecked,
        level: "warning",
    }
    return new AppError(
        error.message,
        error.code,
        error.displayDetail,
        error.technicalDetail,
        error.level
    )
}

/**
 * Fall back error for unrecognised errors. This should be used as a catch all.
 * @returns {AppError} - the generic error parse into an AppError
 */
export const pydanticValidationError = (): AppError => {
    const error: IAppError = {
        message: "Ah! We are unable to perform this action for you",
        code: 422,
        displayDetail: GENERIC_DISPLAY_DETAIL,
        technicalDetail: PYDANTIC_ERROR_TECHNICAL_DETAIL,
        level: "warning",
    }
    return new AppError(
        error.message,
        error.code,
        error.displayDetail,
        error.technicalDetail,
        error.level
    )
}

/**
 * Fall back error for unrecognised errors. This should be used as a catch all.
 * @returns {AppError} - the generic error parse into an AppError
 */
export const permissionDeniedError = (
    displayDetail: string = NO_PERMISSION_DISPLAY_DETAIL
): AppError => {
    const error: IAppError = {
        message: "It looks like you do not have permission to do this!",
        code: 403,
        displayDetail: displayDetail
            ? `${displayDetail}.`
            : NO_PERMISSION_TECHNICAL_DETAIL,
        technicalDetail: NO_PERMISSION_TECHNICAL_DETAIL,
        level: "warning",
    }
    return new AppError(
        error.message,
        error.code,
        error.displayDetail,
        error.technicalDetail,
        error.level
    )
}

/**
 * Thrown when API response fails validation. Called in mappers.
 * @returns {AppError} - the generic error parse into an AppError
 */
export const invalidApiDataError = (technicalDetail: string): AppError => {
    const technicalDetailChecked =
        technicalDetail ||
        "Technical reason for this error was not able to be extracted"
    const error: IAppError = {
        message: "Ah! There was an issue loading this for you",
        code: 400,
        displayDetail: `Please flag this immediately to ${SUPPORT_REFER_ENTITY}`,
        technicalDetail: technicalDetailChecked,
        level: "warning",
    }
    return new AppError(
        error.message,
        error.code,
        error.displayDetail,
        error.technicalDetail,
        error.level
    )
}

/**
 * Thrown when the app enters an invalid state.
 * @returns {AppError} - the generic error parse into an AppError
 */
export const internalAppError = (technicalDetail: string): AppError => {
    const technicalDetailChecked =
        technicalDetail ||
        "The app entered an invalid state. It was not possible to extract the technical reason for this error"
    const error: IAppError = {
        message: "Ah! There something went wrong!",
        code: 500,
        displayDetail: `Please flag this immediately to ${SUPPORT_REFER_ENTITY}`,
        technicalDetail: technicalDetailChecked,
        level: "warning",
    }
    return new AppError(
        error.message,
        error.code,
        error.displayDetail,
        error.technicalDetail,
        error.level
    )
}

/**
 * Fall back error for unrecognised errors. This should be used as a catch all.
 * @returns {AppError} - the generic error parse into an AppError
 */
export const unrecognisedError = (): AppError => {
    const error: IAppError = {
        message: "Whoops! Something went wrong",
        code: 500,
        displayDetail: GENERIC_DISPLAY_DETAIL,
        technicalDetail: GENERIC_UNRECOGNISED_TECHNICAL_DETAIL,
        level: "error",
    }
    return new AppError(
        error.message,
        error.code,
        error.displayDetail,
        error.technicalDetail,
        error.level
    )
}
